import { CartesianGrid, Line, LineChart, Tooltip, XAxis, YAxis } from "recharts";

import { ChartFrame } from "@/components/data";

import { money } from "./format";
import { SCOPE_LABEL, SCOPE_TONE, WEEKLY_COPY, type OrderLine, type OrderPoint, type Scope } from "./margin_weeks";

/** The only numeric conversion in this file positions the points: layout, never a figure shown as fact. The legend and the tooltips carry the API's strings. */
const n = (value: string | null): number | null => (value == null ? null : Number(value));

/** One point per week; a week a line does not carry is null, so the line breaks there (never bridged). */
function rows(weeks: string[], lines: OrderLine[]) {
  return weeks.map((week) => {
    const row: Record<string, number | string | null> = { week: week.slice(5), iso: week };
    for (const line of lines) row[line.scope] = n(line.series.find((p) => p.iso_week === week)?.margin_pct ?? null);
    return row;
  });
}

export function pointNote(scope: Scope, p: OrderPoint, currency: string): string {
  const base = `${p.iso_week} · ${SCOPE_LABEL[scope]}: ${p.orders} orders · net revenue ${money(p.revenue, currency)} · profit ${money(p.profit, currency)} · margin ${p.margin_pct == null ? "none" : `${p.margin_pct}%`}`;
  if (scope !== "company") return base;
  const stores = p.stores_included.map((s) => SCOPE_LABEL[s as Scope] ?? s).join(", ");
  return `${base} · ECB rate of ${p.fx_rate_date ?? "none"} · stores: ${stores || "none"}${p.missing.some((m) => m.endsWith("AED")) ? ` · ${WEEKLY_COPY.gccNotConverted}` : ""}`;
}

/** The per-store lines in the store currency and the company line in GBP (owner ruling W-B), under the moves list. */
export function StoreLinesChart({ weeks, lines }: { weeks: string[]; lines: OrderLine[] }) {
  const data = rows(weeks, lines);
  // The hover tooltip shows the API's margin string for the point (owner ruling 2026-09-24), never the plotted number.
  const shown = new Map<string, string>();
  for (const line of lines) for (const p of line.series) if (p.margin_pct != null) shown.set(`${line.scope}|${p.iso_week}`, `${p.margin_pct}%`);
  const points = lines.reduce((sum, l) => sum + l.series.filter((p) => weeks.includes(p.iso_week)).length, 0);
  return (
    <div className="flex flex-col gap-2">
      <ChartFrame title={WEEKLY_COPY.linesTitle} height={240} pointCount={points} emptyMessage={WEEKLY_COPY.noLines}>
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 0, left: 0 }}>
          <CartesianGrid vertical={false} stroke="var(--color-ks-grey-3)" />
          <XAxis dataKey="week" tickLine={false} axisLine={{ stroke: "var(--color-ks-grey-3)" }} tick={{ fontSize: 11, fill: "var(--color-ks-grey-1)" }} />
          <YAxis tickFormatter={(v: number) => `${v}%`} tickLine={false} axisLine={false} tick={{ fontSize: 11, fill: "var(--color-ks-grey-1)" }} width={40} />
          <Tooltip
            cursor={{ stroke: "var(--color-ks-grey-3)" }}
            contentStyle={{ fontSize: 12, borderColor: "var(--color-ks-grey-3)" }}
            labelFormatter={(_label, payload) => String((payload?.[0]?.payload as { iso?: string } | undefined)?.iso ?? _label)}
            formatter={(_value, name, item) => [shown.get(`${String(name)}|${String((item.payload as { iso?: string }).iso)}`) ?? "none", SCOPE_LABEL[name as Scope]]}
          />
          {lines.map((line) => (
            <Line key={line.scope} type="monotone" dataKey={line.scope} stroke={SCOPE_TONE[line.scope]} strokeWidth={line.scope === "company" ? 3 : 1.8}
              strokeDasharray={line.scope === "shopify_mena" ? "4 3" : undefined} dot={{ r: 3 }} connectNulls={false} isAnimationActive={false} />
          ))}
        </LineChart>
      </ChartFrame>
      <ul className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-ks-grey-1">
        {lines.map((line) => {
          const count = line.series.filter((p) => weeks.includes(p.iso_week)).length;
          return <li key={line.scope} className="flex items-center gap-1"><span aria-hidden className="inline-block h-0.5 w-4" style={{ background: SCOPE_TONE[line.scope] }} />{SCOPE_LABEL[line.scope]}{line.scope === "company" ? "" : ` (${line.currency})`}{count === 0 ? ` · ${WEEKLY_COPY.noWeeks}` : ""}</li>;
        })}
        <li>{WEEKLY_COPY.linesLegend}</li>
      </ul>
      <ul className="sr-only">
        {lines.flatMap((line) => line.series.filter((p) => weeks.includes(p.iso_week)).map((p) => <li key={`${line.scope}-${p.iso_week}`}>{pointNote(line.scope, p, line.currency)}</li>))}
      </ul>
    </div>
  );
}
